// sessionStorage: max 5M

// ルートキー
const ROOT_KEY = 'session:BF';

/**
 * セッションの値を取得する。
 * @param {string} key 取得する値のキー(画面ID等)
 * @return {any} セッションに保存した値
 */
export function getSessionObject(key) {
  const allValues = storageValue();
  return allValues[key];
}

/**
 * セッションに値を保存する。
 * @param {string} key 保存する値のキー(画面ID等)
 * @param {any} value セッションに保存する値
 */
export function setSessionObject(key, value) {
  if (!key) {
    return;
  }
  const allValues = storageValue();
  allValues[key] = value;
  const json = JSON.stringify(allValues);
  sessionStorage.setItem(ROOT_KEY, json);
}

/**
 * セッションの値を削除する。
 * @param {string} key 削除する値のキー(画面ID等)
 */
export function removeSession(key) {
  const allValues = storageValue();
  delete allValues[key];
  const json = JSON.stringify(allValues);
  sessionStorage.setItem(ROOT_KEY, json);
}

/**
 * セッションの値を全て削除する。
 */
export function clearSession() {
  sessionStorage.removeItem(ROOT_KEY);
}

/**
 * セッションストレージの値を取得する。
 * @return {Object} 取得した値 (未設定/値不正の場合は空オブジェクト)
 */
function storageValue() {
  try {
    const json = sessionStorage.getItem(ROOT_KEY);
    const value = JSON.parse(json);
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return value;
    }
  } catch (e) {
    console.error('Failed to load BF session storage value', e);
  }
  return {};
}

/**
 * locationからクエリパラメータを取得し、セッション情報として登録する。
 *
 * @param {object} location location
 * @param {(string|object)[]|(string|object)} keys セッションに指定するクエリパラメータのキー情報
 * @param {string} [gmnId] 画面ID(セッションキー)。省略時はlocationのpathnameから生成
 * @param {object} [otherItems] クエリパラメータ以外でセッションに登録する情報
 */
export function setSessionFromLocation(location, keys, gmnId, otherItems={}) {
  // locationにsearchが指定されていない場合は終了
  const search = location?.search;
  if (!search) {
    return;
  }

  // キー情報が未指定の場合は終了
  if (!keys) {
    console.warn('setSessionFromLocation キー情報指定なし');
    return;
  }

  // キー情報が配列でない場合は配列化
  if (!Array.isArray(keys)) {
    keys = [keys];
  }

  // クエリパラメータ情報を生成
  const queryParam = new URLSearchParams(search);

  // クエリパラメータ以外でセッションに設定する情報を設定しておく
  const sessionItem = {...otherItems};

  // クエリパラメータの情報をオブジェクト化
  for (const keyItem of keys) {
    const keyNames = getKeyName(keyItem);

    for (const keyInfo of keyNames) {
      const queryParamKey = keyInfo.queryParamKey;
      const sessionKey = keyInfo.sessionKey;

      const value = queryParam.get(queryParamKey);
      if (value) {
        sessionItem[sessionKey] = value;
      }
    }
  }

  // 画面ID未指定時はlocationのpathnameから生成
  if (!gmnId) {
    const pathnameStrs = location.pathname.split('/');
    gmnId = pathnameStrs[pathnameStrs.length - 1];
  }

  // セッションに登録
  setSessionObject(gmnId, sessionItem);
}

/**
 * クエリパラメータ/セッションキー情報の生成
 *
 * @param {(string|object)} keyItem キー情報
 * @return {object[]}
 */
function getKeyName(keyItem) {
  // 文字列の場合はそのままクエリパラメータ/セッションキーとして返却
  if (typeof keyItem === 'string') {
    return [{
      queryParamKey: keyItem,
      sessionKey: keyItem,
    }];
  }

  // そうでない場合、オブジェクトのキーをクエリパラメータキー、値をセッションキーとして返却
  const keyNames = [];
  for (const [key, value] of Object.entries(keyItem)) {
    keyNames.push({
      queryParamKey: key,
      sessionKey: value,
    });
  }

  return keyNames;
}
